import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'react';
import { SocketContext } from '../../../context/socket';
import {
  normalizeSource,
  maskId,
  bucketRegion,
  bucketDevice,
  incrementCount,
  pruneOld,
  extractReferrerHost,
} from '../utils/trafficUtils';

const STORAGE_ALL = 'galaxyGate_allTime';
const STORAGE_24H = 'galaxyGate_24h';
const WINDOW_24H = 24 * 60 * 60 * 1000;
const SURGE_WINDOW = 3000;
const SURGE_THRESHOLD = 5;

export default function useGalaxyGateEngine() {
  const { socket } = useContext(SocketContext) || {};
  const [liveCount, setLiveCount] = useState(0);
  const [unique24h, setUnique24h] = useState(0);
  const [uniqueAllTime, setUniqueAllTime] = useState(0);
  const [trafficSources, setTrafficSources] = useState({});
  const [regionStats, setRegionStats] = useState({});
  const [deviceStats, setDeviceStats] = useState({});
  const [lastArrivals, setLastArrivals] = useState([]);
  const [surgeDetected, setSurgeDetected] = useState(false);
  const [recentEvents, setRecentEvents] = useState([]);

  const allTimeRef = useRef(new Set());
  const seen24hRef = useRef(new Map());
  const arrivalBufferRef = useRef([]);
  const surgeTimerRef = useRef(null);

  // hydrate storage
  useEffect(() => {
    try {
      const storedAll = localStorage.getItem(STORAGE_ALL);
      if (storedAll) {
        const arr = JSON.parse(storedAll);
        if (Array.isArray(arr)) allTimeRef.current = new Set(arr);
      }
      const stored24 = localStorage.getItem(STORAGE_24H);
      if (stored24) {
        const entries = JSON.parse(stored24);
        if (Array.isArray(entries)) {
          const now = Date.now();
          entries.forEach(([sid, ts]) => {
            if (sid && ts && now - ts <= WINDOW_24H) {
              seen24hRef.current.set(sid, ts);
            }
          });
        }
      }
      setUniqueAllTime(allTimeRef.current.size);
      setUnique24h(seen24hRef.current.size);
    } catch {
      /* ignore */
    }
  }, []);

  const persist = useCallback(() => {
    try {
      localStorage.setItem(
        STORAGE_ALL,
        JSON.stringify(Array.from(allTimeRef.current)),
      );
      localStorage.setItem(
        STORAGE_24H,
        JSON.stringify(Array.from(seen24hRef.current.entries())),
      );
    } catch {
      /* ignore */
    }
  }, []);

  const noteArrival = useCallback(
    (guest) => {
      const now = Date.now();
      const sid =
        guest.sessionId ||
        guest.session_id ||
        guest.guestId ||
        guest._id ||
        `guest-${Math.random().toString(36).slice(2, 8)}`;
      if (!allTimeRef.current.has(sid)) {
        allTimeRef.current.add(sid);
      }
      seen24hRef.current = pruneOld(seen24hRef.current, WINDOW_24H, now);
      seen24hRef.current.set(sid, now);
      setUniqueAllTime(allTimeRef.current.size);
      setUnique24h(seen24hRef.current.size);
      setLastArrivals((prev) => {
        const entry = {
          sessionId: sid,
          fullId: sid,
          label: maskId(sid),
          at: now,
          source: normalizeSource(guest),
          region: bucketRegion(guest),
          referrer: extractReferrerHost(guest),
          device: bucketDevice(guest),
        };
        const deduped = prev.filter((p) => p.sessionId !== sid);
        const next = [entry, ...deduped].slice(0, 10);
        return next;
      });
      // surge detection
      arrivalBufferRef.current.push(now);
      const cutoff = now - SURGE_WINDOW;
      arrivalBufferRef.current = arrivalBufferRef.current.filter((t) => t >= cutoff);
      if (arrivalBufferRef.current.length >= SURGE_THRESHOLD) {
        setSurgeDetected(true);
        if (surgeTimerRef.current) clearTimeout(surgeTimerRef.current);
        surgeTimerRef.current = setTimeout(() => setSurgeDetected(false), 2000);
      }
      // sparkline
      setRecentEvents((prev) =>
        [...prev, { t: now, v: liveCount || 0 }].slice(-60),
      );
      persist();
    },
    [liveCount, persist],
  );

  const handleSnapshot = useCallback(
    (payload) => {
      const guests = payload?.guests || payload?.sessions || payload?.guestSessions || {};
      const entries = Array.isArray(guests)
        ? guests
        : Object.entries(guests).map(([, v]) => v);
      setLiveCount(payload?.count ?? entries.length ?? 0);
      const now = Date.now();
      const nextSources = {};
      const nextRegions = {};
      const nextDevices = {};
      entries.forEach((g) => {
        const source = normalizeSource(g);
        const region = bucketRegion(g);
        const device = bucketDevice(g);
        Object.assign(nextSources, incrementCount(nextSources, source));
        Object.assign(nextRegions, incrementCount(nextRegions, region));
        Object.assign(nextDevices, incrementCount(nextDevices, device));
        noteArrival(g);
      });
      setTrafficSources(nextSources);
      setRegionStats(nextRegions);
      setDeviceStats(nextDevices);
      // prune 24h window and persist
      seen24hRef.current = pruneOld(seen24hRef.current, WINDOW_24H, now);
      setUnique24h(seen24hRef.current.size);
      persist();
    },
    [noteArrival, persist],
  );

  const handleDiff = useCallback(
    (payload) => {
      const add = payload?.add || [];
      const count = payload?.count;
      const added = Array.isArray(add)
        ? add
        : Object.entries(add).map(([, v]) => v);
      if (typeof count === 'number') setLiveCount(count);
      added.forEach((g) => {
        noteArrival(g);
        setTrafficSources((prev) =>
          incrementCount(prev, normalizeSource(g)),
        );
        setRegionStats((prev) => incrementCount(prev, bucketRegion(g)));
        setDeviceStats((prev) => incrementCount(prev, bucketDevice(g)));
      });
      const now = Date.now();
      seen24hRef.current = pruneOld(seen24hRef.current, WINDOW_24H, now);
      setUnique24h(seen24hRef.current.size);
      persist();
    },
    [noteArrival, persist],
  );

  useEffect(() => {
    if (!socket) return undefined;
    socket.on('guest:snapshot', handleSnapshot);
    socket.on('guest:diff', handleDiff);
    return () => {
      socket.off('guest:snapshot', handleSnapshot);
      socket.off('guest:diff', handleDiff);
    };
  }, [socket, handleSnapshot, handleDiff]);

  return {
    liveCount,
    unique24h,
    uniqueAllTime,
    trafficSources,
    regionStats,
    deviceStats,
    lastArrivals,
    surgeDetected,
    recentEvents,
  };
}
